import { z } from "zod";

/**
 * One instance as the engine scored it when it placed a generation
 * (elixir_engine `Router.decision/4`): its free and total slots across its
 * shards, the load penalty and health state its worker reports earned, the
 * request's affinity bonus, and its v1 (`free share + locality + RTT`) and
 * v2 (`(v1 + affinity) × (1 − penalty)`) scores.
 */
export const EngineRoutingDecisionInstanceSchema = z.object({
  /** Null for a shard with no instance membership. */
  instanceId: z.string().nullable(),
  freeSlots: z.number().int(),
  maxSlots: z.number().int(),
  penalty: z.number(),
  degraded: z.boolean(),
  affinity: z.number(),
  scoreV1: z.number(),
  scoreV2: z.number(),
});
export type EngineRoutingDecisionInstance = z.infer<
  typeof EngineRoutingDecisionInstanceSchema
>;

/** The chosen instance's latest worker report as scored (`InstanceLoad`). */
export const EngineRoutingDecisionReportSchema = z.object({
  /** Every metric id the engine derived from the report. */
  metrics: z.record(z.number()),
  /** Each pressure's ramp before its weight. */
  pressures: z.record(z.number()),
  /** Whether each health signal fired on the report. */
  signals: z.record(z.boolean()),
  reportAgeMs: z.number(),
});

/**
 * The engine's routing decision for one placed generation, published on
 * `engine-routing-decision` and written to ClickHouse
 * `engine_routing_decisions` by llm-ops-consumer.
 */
export const EngineRoutingDecisionMessageSchema = z.object({
  generationId: z.string().min(1),
  /** The routing model (`endpoints.public_model_identifier`). */
  model: z.string(),
  /** Epoch milliseconds. */
  routedAt: z.number(),
  routingVersion: z.string(),
  healthGate: z.boolean(),
  candidateCount: z.number().int(),
  chosen: EngineRoutingDecisionInstanceSchema.extend({
    rankV2: z.number().int(),
    /** Null when the instance had no fresh worker report. */
    report: EngineRoutingDecisionReportSchema.nullable(),
  }),
  /** The best-scoring other instances, best first. */
  runnersUp: z.array(EngineRoutingDecisionInstanceSchema),
});
export type EngineRoutingDecisionMessage = z.infer<
  typeof EngineRoutingDecisionMessageSchema
>;
